import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { ClipboardList, Copy, Download, Plus, Printer, RefreshCw, Save, Trash2 } from 'lucide-react';
import { qrSvg } from '@/lib/qr';
import { downloadText } from '@/lib/download';
import { fmtDateInput } from '@/lib/format';
import { useCompetition, useSync, useTurnier } from '@/store/TournamentProvider';
import { useAuth } from '@/store/AuthProvider';
import {
  MAX_FIELDS,
  adminRegistrations,
  defaultDeadline,
  deleteTemplate,
  fromLocalInput,
  listTemplates,
  newFieldId,
  saveTemplate,
  setRegistration,
  templateFrom,
  toLocalInput,
  type RegTemplate,
  type RegistrationBody,
  type RegistrationConfig,
  type RegistrationField,
} from '@/store/registration';
import { Button, Card, Field, Input, Select, Textarea, cx } from '@/ui';
import { confirmDialog, copyDialog } from '@/ui/dialogs';
import { locale, useT } from '@/i18n';
import { fehlerText } from '@/store/plan';
import { anmeldeLinkTeilen } from '@/store/sync';
import { Gate, useFreigabe } from '@/components/ProMarke';

/** Einstellungen → „Anmeldung“ (docs/15): ein-/ausschalten, Frist, Texte, Zusatzfragen, Vorlagen, Link */
export function RegistrationPanel() {
  const t = useT();
  const { tournament } = useTurnier();
  const sync = useSync();
  const comp = useCompetition();
  const auth = useAuth();
  const [cfg, setCfg] = useState<RegistrationConfig | null | undefined>(undefined);
  const [count, setCount] = useState(0);
  const [form, setForm] = useState<RegistrationBody | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Vorlagen im Konto (docs/15 §6) — nur angemeldet, sonst gibt es kein Konto, das sie aufhebt
  const [vorlagen, setVorlagen] = useState<RegTemplate[]>([]);
  const [vorlageName, setVorlageName] = useState('');
  const [vorlageSpeichern, setVorlageSpeichern] = useState(false);
  // Anmeldung öffnen braucht eine Stufe ≠ frei (docs/21) — Bearbeiten und Schließen bleiben
  const cloudErlaubt = useFreigabe('cloud');

  /*
   * Die Sync-Konfiguration ist nach jedem Abgleich (alle paar Sekunden) ein
   * neues Objekt. Hing `load` direkt daran, fragte die Karte im selben Takt
   * den Server — darum über eine Ref, und neu geladen wird nur, wenn ein
   * anderes Turnier offen ist oder die Cloud gerade eingeschaltet wurde.
   */
  const syncRef = useRef(sync.cfg);
  syncRef.current = sync.cfg;
  const hatSync = !!sync.cfg;
  const load = useCallback(async () => {
    const s = syncRef.current;
    if (!s) return;
    try {
      const r = await adminRegistrations(s);
      setCfg(r.config);
      setCount(r.registrations.length);
      setError(null);
    } catch (e) {
      // Den letzten bekannten Stand behalten: Mit `null` stand hier „Aus" samt
      // „Anmeldung einrichten" — und das hätte beim Speichern die vorhandenen
      // Zusatzfragen mit Vorgaben überschrieben
      setError(t(fehlerText(e)));
    }
    // `tournament.id`: neu laden, wenn ein anderes Turnier offen ist
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournament.id, hatSync, t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!auth.user) return setVorlagen([]);
    listTemplates()
      .then(setVorlagen)
      .catch(() => setVorlagen([]));
  }, [auth.user]);

  // QR-Code zum Anmelde-Link (Hooks müssen vor dem frühen return stehen)
  const linkForQr = cfg && sync.cfg ? anmeldeLinkTeilen(sync.cfg, cfg.regKey) : '';
  const [qr, setQr] = useState<string>('');
  useEffect(() => {
    if (!linkForQr) return setQr('');
    let alive = true;
    qrSvg(linkForQr, { size: 160 })
      .then((svg) => alive && setQr(svg))
      .catch(() => alive && setQr(''));
    return () => {
      alive = false;
    };
  }, [linkForQr]);

  if (!sync.cfg) {
    return (
      <Card title={t('Anmeldung')}>
        <div className="p-4 text-sm text-muted">
          {t('Teilnehmer melden sich selbst über einen Link an — bis zu einer Frist, die du festlegst. Dafür muss oben')} <b className="text-ink">{t('Cloud-Sync')}</b>{' '}
          {t('aktiviert sein, weil die Anmeldungen auf dem Server landen.')}
        </div>
      </Card>
    );
  }

  const bodyAus = (src: RegistrationConfig, open = src.open): RegistrationBody => {
    const body: RegistrationBody = { open, deadline: src.deadline, title: src.title, teamSize: src.teamSize };
    if (src.date) body.date = src.date;
    if (src.location) body.location = src.location;
    if (src.note) body.note = src.note;
    if (src.maxTeams) body.maxTeams = src.maxTeams;
    if (src.fields?.length) body.fields = src.fields.map((f) => ({ ...f }));
    return body;
  };

  const startEdit = () => {
    if (cfg) return setForm(bodyAus(cfg));
    const base: RegistrationBody = { open: true, deadline: defaultDeadline(tournament.date), title: tournament.name, teamSize: comp.ruleSet.teamSize };
    if (tournament.date) base.date = tournament.date;
    if (tournament.location) base.location = tournament.location;
    setForm(base);
  };

  const save = async () => {
    if (!form || !sync.cfg) return;
    if (!form.deadline) return setError(t('Anmeldeschluss fehlt'));
    if (!cfg && form.open && !cloudErlaubt) return;
    if ((form.fields ?? []).some((f) => !f.label.trim())) return setError(t('Jede Zusatzfrage braucht eine Beschriftung — leere Zeilen löschen.'));
    setBusy(true);
    try {
      const r = await setRegistration(sync.cfg, form);
      setCfg(r);
      setForm(null);
      setError(null);
    } catch (e) {
      setError(t(fehlerText(e)));
    } finally {
      setBusy(false);
    }
  };

  const toggleOpen = async () => {
    if (!cfg || !sync.cfg || (!cfg.open && !cloudErlaubt)) return;
    setBusy(true);
    try {
      setCfg(await setRegistration(sync.cfg, bodyAus(cfg, !cfg.open)));
    } catch (e) {
      setError(t(fehlerText(e)));
    } finally {
      setBusy(false);
    }
  };

  // --- Zusatzfragen im Formular ---
  const felder = form?.fields ?? [];
  const setFelder = (next: RegistrationField[]) => form && setForm({ ...form, fields: next });
  const feldHinzu = () => setFelder([...felder, { id: newFieldId(), label: '', type: 'bool' }]);
  const feldPatch = (i: number, patch: Partial<RegistrationField>) =>
    setFelder(
      felder.map((f, k) => {
        if (k !== i) return f;
        const next = { ...f, ...patch };
        if (!next.required) delete next.required;
        return next;
      }),
    );
  const feldWeg = (i: number) => setFelder(felder.filter((_, k) => k !== i));

  // --- Vorlagen ---
  const vorlageLaden = (id: string) => {
    const v = vorlagen.find((x) => x.id === id);
    if (!v || !form) return;
    const next: RegistrationBody = { ...form, teamSize: v.data.teamSize, fields: v.data.fields.map((f) => ({ ...f })) };
    if (v.data.title) next.title = v.data.title;
    if (v.data.note) next.note = v.data.note;
    else delete next.note;
    if (v.data.maxTeams) next.maxTeams = v.data.maxTeams;
    else delete next.maxTeams;
    setForm(next);
  };
  const vorlageAblegen = async () => {
    const quelle = form ?? cfg;
    if (!quelle) return;
    const name = vorlageName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const alt = vorlagen.find((v) => v.name.toLowerCase() === name.toLowerCase());
      const v = await saveTemplate(name, templateFrom(quelle), alt?.id);
      setVorlagen([...vorlagen.filter((x) => x.id !== v.id), v].sort((a, b) => a.name.localeCompare(b.name, 'de')));
      setVorlageName('');
      setVorlageSpeichern(false);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const vorlageLoeschen = async (v: RegTemplate) => {
    if (!(await confirmDialog(t('Vorlage „{name}“ löschen?', { name: v.name }), { ok: t('Löschen'), danger: true }))) return;
    try {
      await deleteTemplate(v.id);
      setVorlagen(vorlagen.filter((x) => x.id !== v.id));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const link = cfg && sync.cfg ? anmeldeLinkTeilen(sync.cfg, cfg.regKey) : '';
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      await copyDialog(link);
    }
  };
  const deadlinePassed = !!cfg && Date.now() > cfg.deadline;
  const status = !cfg ? 'aus' : !cfg.open ? 'geschlossen' : deadlinePassed ? 'Frist vorbei' : 'offen';
  const TYP_LABEL: Record<RegistrationField['type'], string> = { bool: 'Kästchen', number: 'Zahl', text: 'Freitext' };

  const vorlagenZeile = auth.user && (
    <div className="border border-line2 px-3 py-2 grid gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="label">{t('Vorlagen')}</span>
        {vorlagen.length > 0 && form && (
          <Select className="w-auto text-sm py-1" value="" onChange={(e) => vorlageLaden(e.target.value)} aria-label={t('Vorlage laden')}>
            <option value="">{t('Vorlage laden …')}</option>
            {vorlagen.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name} · {t('{n} Fragen', { n: v.data.fields.length })}
              </option>
            ))}
          </Select>
        )}
        {!vorlageSpeichern ? (
          <Button size="sm" variant="ghost" onClick={() => setVorlageSpeichern(true)}>
            <Save size={13} /> {t('Als Vorlage speichern')}
          </Button>
        ) : (
          <form
            className="flex gap-2 items-center"
            onSubmit={(e) => {
              e.preventDefault();
              void vorlageAblegen();
            }}
          >
            <Input
              className="w-48 py-1 text-sm"
              autoFocus
              placeholder={t('Name der Vorlage')}
              value={vorlageName}
              onChange={(e) => setVorlageName(e.target.value)}
              aria-label={t('Name der Vorlage')}
            />
            <Button size="sm" type="submit" disabled={busy || !vorlageName.trim()}>
              {t('Speichern')}
            </Button>
            <Button size="sm" variant="ghost" type="button" onClick={() => setVorlageSpeichern(false)}>
              {t('Abbrechen')}
            </Button>
          </form>
        )}
      </div>
      {vorlagen.length > 0 && (
        <ul className="m-0 p-0 list-none flex flex-wrap gap-2 text-xs">
          {vorlagen.map((v) => (
            <li key={v.id} className="border border-line px-2 py-1 flex items-center gap-2">
              <span>{v.name}</span>
              <button type="button" className="text-dim hover:text-red cursor-pointer" aria-label={t('Vorlage „{name}“ löschen', { name: v.name })} onClick={() => void vorlageLoeschen(v)}>
                <Trash2 size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <span className="text-xs text-dim">{t('Eine Vorlage merkt sich Titel, Hinweis, Teamgröße, Limit und Zusatzfragen in deinem Konto — Frist, Datum und Ort sind je Turnier neu.')}</span>
    </div>
  );

  return (
    <Card title={t('Anmeldung')} aside={cfg !== undefined && <span className={`label ${status === 'offen' ? 'text-live' : 'text-dim'}`}>{t(status)}</span>}>
      <div className="p-4 grid gap-4 text-sm">
        {cfg === undefined && !error && <div className="text-dim">{t('Lade …')}</div>}
        {error && (
          <div className="text-err text-xs" role="alert">
            {error}
          </div>
        )}

        {cfg === null && !form && (
          <>
            <p className="m-0 text-muted">
              {t(
                'Aus. Mit einem Anmelde-Link tragen sich Teams selbst ein — Name, Spieler, Verein, Kontakt. Bis zum Anmeldeschluss können sie ihre Anmeldung auch wieder zurückziehen. Du übernimmst die Anmeldungen dann unter „Teams“ mit einem Klick.',
              )}
            </p>
            <div>
              <Button variant="primary" onClick={startEdit}>
                <ClipboardList size={14} /> {t('Anmeldung einrichten')}
              </Button>
            </div>
          </>
        )}

        {cfg && !form && (
          <>
            <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 font-mono text-[12px]">
              <span className="text-dim">{t('Titel')}</span>
              <span>{cfg.title}</span>
              <span className="text-dim">{t('Anmeldeschluss')}</span>
              <span className={deadlinePassed ? 'text-warn' : ''}>
                {new Date(cfg.deadline).toLocaleString(locale(), { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
              <span className="text-dim">{t('Teams')}</span>
              <span>
                {cfg.maxTeams
                  ? t('{teams} · maximal {n}, danach Warteliste', { teams: cfg.teamSize === 1 ? t('Einzel') : t('{n}er-Teams', { n: cfg.teamSize }), n: cfg.maxTeams })
                  : t('{teams} · ohne Limit', { teams: cfg.teamSize === 1 ? t('Einzel') : t('{n}er-Teams', { n: cfg.teamSize }) })}
              </span>
              {cfg.fields && cfg.fields.length > 0 && (
                <>
                  <span className="text-dim">{t('Zusatzfragen')}</span>
                  <span>{cfg.fields.map((f) => `${f.label}${f.required ? ' *' : ''}`).join(' · ')}</span>
                </>
              )}
              <span className="text-dim">{t('Anmeldungen')}</span>
              <span>
                {count} ·{' '}
                <Link to={`/t/${tournament.id}/teams`} className="text-blue">
                  {t('unter Teams übernehmen')}
                </Link>
              </span>
            </div>
            <div className="grid grid-cols-[auto_1fr] gap-4 items-start">
              <div data-qr className="bg-white p-1.5 border border-line" style={{ width: 132, height: 132 }} dangerouslySetInnerHTML={{ __html: qr }} title={t('QR-Code zum Anmelde-Link')} />
              <div className="grid gap-3">
                <Field
                  label={t('Anmelde-Link für Teilnehmer')}
                  hint={t('Per WhatsApp, Mail oder auf der Vereinsseite teilen. Wer ihn öffnet, sieht Turnier, Frist und die angemeldeten Teams (ohne Kontaktdaten) und kann sich eintragen.')}
                >
                  <div className="flex gap-2">
                    <Input readOnly value={link} onFocus={(e) => e.target.select()} className="font-mono text-xs" />
                    <Button onClick={copy}>
                      <Copy size={14} /> {copied ? t('Kopiert') : t('Kopieren')}
                    </Button>
                  </div>
                </Field>
                <div className="flex gap-2 flex-wrap">
                  <Link to={`/plakat/${tournament.id}`} className="no-underline">
                    <Button>
                      <Printer size={14} /> {t('Plakat / Flyer mit QR-Code')}
                    </Button>
                  </Link>
                  <Button variant="ghost" disabled={!qr} onClick={() => qrSvg(link, { size: 1024 }).then((svg) => downloadText(`airmail-anmeldung-qr-${tournament.id}.svg`, svg, 'image/svg+xml'))}>
                    <Download size={14} /> {t('QR-Code speichern')}
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={startEdit}>{t('Bearbeiten')}</Button>
              {cfg.open ? (
                <Button onClick={toggleOpen} disabled={busy}>
                  {t('Anmeldung schließen')}
                </Button>
              ) : (
                <Gate feature="cloud">
                  <Button onClick={toggleOpen} disabled={busy}>
                    {t('Anmeldung öffnen')}
                  </Button>
                </Gate>
              )}
              <Button variant="ghost" onClick={() => void load()}>
                <RefreshCw size={14} /> {t('Aktualisieren')}
              </Button>
            </div>
            {vorlagenZeile}
          </>
        )}

        {form && (
          <div className="grid gap-3">
            {vorlagenZeile}
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label={t('Titel (für Teilnehmer)')}>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </Field>
              <Field label={t('Anmeldeschluss')} hint={t('Vorbelegt: Tag vor dem Turnier, 23:59')}>
                <Input type="datetime-local" value={toLocalInput(form.deadline)} onChange={(e) => setForm({ ...form, deadline: fromLocalInput(e.target.value) })} />
              </Field>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Field label={t('Datum')} hint={fmtDateInput(form.date) || undefined}>
                <Input type="date" value={form.date ?? ''} onChange={(e) => setForm({ ...form, date: e.target.value || undefined })} />
              </Field>
              <Field label={t('Ort')}>
                <Input value={form.location ?? ''} onChange={(e) => setForm({ ...form, location: e.target.value || undefined })} />
              </Field>
              <Field label={t('Teamgröße')}>
                <Select value={form.teamSize} onChange={(e) => setForm({ ...form, teamSize: Number(e.target.value) })}>
                  {[1, 2, 3, 4].map((n) => (
                    <option key={n} value={n}>
                      {n === 1 ? t('Einzel') : t('{n} Spieler', { n })}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('Maximal Teams')} hint={t('leer = kein Limit')}>
                <Input type="number" min={1} value={form.maxTeams ?? ''} onChange={(e) => setForm({ ...form, maxTeams: Number(e.target.value) > 0 ? Number(e.target.value) : undefined })} />
              </Field>
            </div>
            <Field label={t('Hinweis für Teilnehmer (optional)')} hint={t('Startgeld, Beginn, Ablauf, Kontakt …')}>
              <Textarea rows={3} value={form.note ?? ''} onChange={(e) => setForm({ ...form, note: e.target.value || undefined })} />
            </Field>

            {/*
              Zusatzfragen (docs/15 §6): Die Grundfelder — Spieler, Teamname,
              Verein, E-Mail, Telefon, Bemerkung — stehen immer im Formular.
              Hier hängt die Leitung eigene Zeilen an: Essen, Übernachtung,
              Regeln gelesen … Die Antworten wandern mit ins Team und stehen
              unter „Teams" als eigene Spalten.
            */}
            <Field
              as="div"
              label={t('Zusatzfragen (optional)')}
              hint={t(
                'Kästchen zum Ankreuzen, eine Zahl (z. B. „Personen zum Essen“) oder ein Freitext. „Pflicht“ heißt: ohne Antwort geht die Anmeldung nicht durch. Die Antworten stehen unter „Teams“ als Spalten.',
              )}
            >
              <div className="grid gap-2" data-tour="reg-fields">
                {felder.map((f, i) => (
                  <div key={f.id} className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-center border border-line2 px-2 py-2">
                    <Input value={f.label} placeholder={t('Frage, z. B. „Essen am Abend“')} aria-label={t('Frage {n}', { n: i + 1 })} onChange={(e) => feldPatch(i, { label: e.target.value })} />
                    <Select className="w-auto" value={f.type} aria-label={t('Art der Frage {n}', { n: i + 1 })} onChange={(e) => feldPatch(i, { type: e.target.value as RegistrationField['type'] })}>
                      {(['bool', 'number', 'text'] as const).map((k) => (
                        <option key={k} value={k}>
                          {t(TYP_LABEL[k])}
                        </option>
                      ))}
                    </Select>
                    <label className={cx('inline-flex items-center gap-1.5 text-xs whitespace-nowrap cursor-pointer', f.required ? 'text-ink' : 'text-dim')}>
                      <input type="checkbox" checked={!!f.required} onChange={(e) => feldPatch(i, { required: e.target.checked })} /> {t('Pflicht')}
                    </label>
                    <Button size="sm" variant="ghost" type="button" aria-label={t('Frage {n} löschen', { n: i + 1 })} onClick={() => feldWeg(i)}>
                      <Trash2 size={13} />
                    </Button>
                  </div>
                ))}
                {felder.length < MAX_FIELDS && (
                  <div>
                    <Button size="sm" type="button" onClick={feldHinzu}>
                      <Plus size={13} /> {t('Frage hinzufügen')}
                    </Button>
                  </div>
                )}
              </div>
            </Field>

            <div className="flex gap-2">
              {/* „Anmeldung starten" öffnet sie zugleich — deshalb dasselbe Gate wie „Anmeldung öffnen" */}
              {cfg ? (
                <Button variant="primary" onClick={save} disabled={busy}>
                  {t('Speichern')}
                </Button>
              ) : (
                <Gate feature="cloud">
                  <Button variant="primary" onClick={save} disabled={busy}>
                    {t('Anmeldung starten')}
                  </Button>
                </Gate>
              )}
              <Button variant="ghost" onClick={() => setForm(null)}>
                {t('Abbrechen')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
